import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useUnit } from "effector-react";
import {
  $InternalStudentCourseState,
  $InternalStudentLessonState,
  $InternalStudentLesson,
  $InternalStudentCourse,
  fetchInternalStudentCourse,
  fetchInternalStudentLesson,
  stageIncrementInternalStudentLesson,
  finishInternalStudentCourse,
} from "@store/internal-study/students-store";
import { setTitle } from "@store/title-store";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { observeDOM } from "@utils/observe-dom";
import clsx from "clsx";
import style from "@scss/pages/study-center.module.scss";

export const StudentsLesson: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();

  const location = useLocation();
  const history = useNavigate();
  const courseState = useUnit($InternalStudentCourseState);
  const course = useUnit($InternalStudentCourse);
  const { isLoading, error, isFetched } = useUnit($InternalStudentLessonState);
  const lesson = useUnit($InternalStudentLesson);
  const [undestend, setUndestend] = useState<boolean>(false);
  const lessonIframe = useRef<HTMLIFrameElement>(null);
  const [timerIntervalId, setTimerInterval] = useState<number>();
  const [countTimer, setCountTimer] = useState<number>(0);
  const timeFinish = 10 * 1000;

  useEffect(() => {
    if (!isFetched || lesson.id !== +lessonId!) {
      fetchInternalStudentLesson(+lessonId!);
    }
    if (!courseState.isFetched) {
      fetchInternalStudentCourse(+courseId!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (lesson && course && course.course_info) {
      setTitle([
        {
          title: "Курсы",
          href: "/",
        },
        {
          title: course.course_info.title,
          href: `/course/${course.id}`,
        },
        {
          title: lesson.title,
          href: `/course/${course.id}/lesson/${lesson.id}`,
        },
      ]);
    }
  }, [lesson, course, location.pathname]);
  useEffect(() => {
    fetchInternalStudentLesson(+lessonId!);
    setUndestend(false);
  }, [lessonId]);

  const startTimerLesson = () => {
    setTimerInterval(
      window.setInterval(() => {
        setCountTimer((prevCount) => prevCount + 10);
      }, 10)
    );
  };

  const updateStageCourseAndNextLesson = () => {
    setUndestend(true);
    stageIncrementInternalStudentLesson({
      id: course.id,
      cb: (err, res) => {
        if (err) {
          console.error(err);
        }
        if (res && res.data.next) {
          setUndestend(false);
          history(`/course/${res.data.id}/lesson/${res.data.next}`);
        }
      },
    });
  };
  useEffect(() => {
    if (countTimer === timeFinish) {
      window.clearInterval(timerIntervalId);
      setTimerInterval(undefined);
      setCountTimer(0);
      updateStageCourseAndNextLesson();
    }
  }, [countTimer, timeFinish, timerIntervalId]);

  const finishCourse = () => {
    setUndestend(true);
    finishInternalStudentCourse({
      id: course.id,
      cb: (err, res) => {
        if (err) {
          console.error(err);
        }
        if (res) {
          setUndestend(false);
          history(`/`);
        }
      },
    });
  };

  const handleLoad = () => {
    try {
      const iframeDoc =
        lessonIframe.current?.contentDocument ||
        lessonIframe.current?.contentWindow?.document;
      const contentCount = iframeDoc?.body.querySelector(
        ".navigation-controls__label"
      );
      if (contentCount === null) {
        startTimerLesson();
      } else {
        observeDOM(
          lessonIframe.current?.contentWindow!,
          contentCount,
          { attributes: true },
          (m) => {
            const checkFinish = contentCount
              ?.getAttribute("aria-label")
              ?.split(" из ");
            if (
              checkFinish &&
              checkFinish.length === 2 &&
              +checkFinish[0] === +checkFinish[1] &&
              !undestend
            ) {
              setTimeout(() => {
                const finishContainer =
                  iframeDoc?.body.querySelector(".quiz-session-view");
                if (finishContainer === null) {
                  updateStageCourseAndNextLesson();
                } else {
                  observeDOM(
                    lessonIframe.current?.contentWindow!,
                    finishContainer,
                    { childList: true },
                    (m) => {
                      const iconFinish = iframeDoc?.body.querySelector(
                        ".slide-object-view-icon-placeholder"
                      );
                      if (iconFinish !== null) {
                        if (
                          iconFinish?.classList.contains(
                            "slide-object-view-icon-placeholder_type_failed"
                          )
                        ) {
                          //Failed
                        } else if (
                          iconFinish?.classList.contains(
                            "slide-object-view-icon-placeholder_type_passed"
                          )
                        ) {
                          finishCourse();
                        }
                      }
                    }
                  );
                }
              }, 5); //Чтобы успел сформироваться HTML внутри iframe, отложено на 5 тиков
            }
          }
        );
      }
    } catch (error) {
      console.error("Ошибка получения доступа к iframe:", error);
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorIndicator />
      ) : (
        <div className={clsx(style.student_lesson)}>
          {countTimer > 0 && (
            <div
              className={clsx(style.student_lesson_timer)}
              style={{ width: `${(countTimer / timeFinish) * 100}%` }}
            />
          )}
          <iframe
            ref={lessonIframe}
            allow="autoplay"
            loading="eager"
            title={lesson.title}
            src={lesson.path}
            onLoad={handleLoad}
          />
        </div>
      )}
    </>
  );
};
